import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { exitFightMode } from './platform/gameMode';
import { LEAGUE_SCREENS, useApp, type Screen } from './state/store';
import { CountryScreen } from './ui/screens/CountryScreen';
import { FaqScreen } from './ui/screens/FaqScreen';
import { GamePlanScreen } from './ui/screens/GamePlanScreen';
import { HomeScreen } from './ui/screens/HomeScreen';
import { LeagueScreen } from './ui/screens/LeagueScreen';
import { LeaguesScreen } from './ui/screens/LeaguesScreen';
import { LockerScreen } from './ui/screens/LockerScreen';
import { MatchmakingScreen } from './ui/screens/MatchmakingScreen';
import { PopupHost } from './ui/screens/PopupHost';
import { RejoinCard } from './ui/screens/RejoinCard';
import { ProfileScreen } from './ui/screens/ProfileScreen';
import { ResultScreen } from './ui/screens/ResultScreen';
import { SettingsScreen } from './ui/screens/SettingsScreen';
import { TrainingScreen } from './ui/screens/TrainingScreen';
import { VsScreen } from './ui/screens/VsScreen';
import { onlineConfigured, serverProgress } from './net/config';
import { AuthSheet } from './ui/components/AuthSheet';

// The fight (PixiJS) loads on demand, so the menus open fast; it is prefetched during matchmaking.
const loadFight = () => import('./fight/FightScreen');
const FightScreen = lazy(() => loadFight().then((m) => ({ default: m.FightScreen })));

/** The online code (Supabase, Colyseus) loads in the background once the menus are up. */
function usePrefetchOnline() {
  useEffect(() => {
    if (!onlineConfigured()) return;
    const id = setTimeout(() => void import('./net/online'), 1500);
    return () => clearTimeout(id);
  }, []);
}

/**
 * Progress comes from the game server (Milestone 4): loaded once the menus are up, and again
 * when the game comes back into view (another device may have changed it).
 */
function useProgressSync() {
  useEffect(() => {
    if (!serverProgress()) return;
    const sync = () => void useApp.getState().syncProgress();
    const id = setTimeout(() => {
      sync();
      void useApp.getState().loadAccount();
    }, 300);
    const onVisible = () => document.visibilityState === 'visible' && sync();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}

/** Menus ?screen= can open directly (with ?lang=, a quick look at a translation). */
const MENU_SCREENS: readonly Screen[] = ['home', 'training', 'profile', 'settings', 'locker', 'faq', 'league', 'leagues', 'plan'];

/** Dev shortcuts: ?fight jumps straight into a quick fight, ?screen=training opens a menu. */
function useDevShortcuts() {
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const menu = params.get('screen') as Screen | null;
    if (params.has('fight')) {
      const s = useApp.getState();
      s.startQuickFight();
      useApp.setState({ screen: 'fight' });
    } else if (menu && MENU_SCREENS.includes(menu)) {
      useApp.getState().go(menu);
    }
  }, []);
}

const SUBSCREENS: readonly Screen[] = ['training', 'profile', 'settings', 'locker', 'faq', ...LEAGUE_SCREENS];
const FIGHT_LOOP: readonly Screen[] = ['matchmaking', 'vs', 'fight'];

/**
 * The phone's Back button: from Training, Profile or Settings it returns Home; during the fight
 * loop it stays in the game (the pause menu is the way out) instead of leaving the site.
 */
function useBackButton() {
  useEffect(() => {
    history.replaceState({ caged: 'home' }, '');
    let last = useApp.getState().screen;
    const unsub = useApp.subscribe((s) => {
      if (s.screen === last) return;
      const needsEntry = SUBSCREENS.includes(s.screen) || FIGHT_LOOP.includes(s.screen);
      const hadEntry = SUBSCREENS.includes(last) || FIGHT_LOOP.includes(last);
      if (needsEntry && !hadEntry) history.pushState({ caged: s.screen }, '');
      last = s.screen;
    });
    const onPop = () => {
      const st = useApp.getState();
      if (LEAGUE_SCREENS.includes(st.screen)) {
        st.leagueBack();
        // Still on a league screen: keep an entry for the next Back.
        if (LEAGUE_SCREENS.includes(useApp.getState().screen)) history.pushState({ caged: useApp.getState().screen }, '');
      } else if (SUBSCREENS.includes(st.screen)) st.go('home');
      else if (FIGHT_LOOP.includes(st.screen)) {
        history.pushState({ caged: st.screen }, '');
        window.dispatchEvent(new Event('caged:back'));
      }
    };
    window.addEventListener('popstate', onPop);
    return () => {
      unsub();
      window.removeEventListener('popstate', onPop);
    };
  }, []);
}

function usePrefetchFight() {
  const screen = useApp((s) => s.screen);
  useEffect(() => {
    if (screen === 'matchmaking' || screen === 'vs') void loadFight();
    // Menus are portrait on phones: leave fullscreen/landscape when the fight loop ends.
    if (SUBSCREENS.includes(screen) || screen === 'home') exitFightMode();
  }, [screen]);
}

/** Each menu's own tab title; Home and the fight loop say what the game is. */
const PAGE_TITLES: Partial<Record<Screen, string>> = {
  training: 'screens.nav.training',
  profile: 'screens.nav.profile',
  settings: 'screens.nav.settings',
  locker: 'screens.locker.title',
  faq: 'faq.link',
  league: 'screens.nav.league',
  leagues: 'screens.leagues.title',
  country: 'screens.leagues.title',
  plan: 'screens.plan.title',
};

/**
 * The tab's title in the player's language: "Training · Caged", or "Caged · Quick 1v1 MMA fights
 * in your browser". index.html carries the English one for search engines and link previews.
 */
function useDocumentTitle() {
  const { t, i18n } = useTranslation();
  const screen = useApp((s) => s.screen);
  useEffect(() => {
    const page = PAGE_TITLES[screen];
    document.title = page ? `${t(page)} · Caged` : `Caged · ${t('meta.tagline')}`;
  }, [screen, t, i18n.language]);
}

/** A tapped league reminder (public/push-sw.js) brings the league up, unless a fight is on. */
function useReminderTaps() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      if ((e.data as { type?: string } | null)?.type !== 'caged:openLeague') return;
      const st = useApp.getState();
      if (!FIGHT_LOOP.includes(st.screen) && st.screen !== 'result') st.openLeague('league');
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);
}

/**
 * The first screen takes the place of the loading skeleton (index.html): its entrance animations
 * jump to their end, so it doesn't fade in a second time over the shapes it replaces.
 */
function useNoEntranceAtStart() {
  useLayoutEffect(() => {
    if (typeof CSSAnimation === 'undefined' || typeof document.getAnimations !== 'function') return;
    for (const a of document.getAnimations()) if (a instanceof CSSAnimation && a.animationName === 'cgRise') a.finish();
  }, []);
}

export function App() {
  useNoEntranceAtStart();
  useDevShortcuts();
  useDocumentTitle();
  useReminderTaps();
  usePrefetchOnline();
  useProgressSync();
  useBackButton();
  usePrefetchFight();
  const screen = useApp((s) => s.screen);
  const rejoin = useApp((s) => !!s.rejoin);
  const authSheet = useApp((s) => s.authSheet);
  const closeAuth = useApp((s) => s.closeAuth);
  return (
    <>
      {screen === 'home' && <HomeScreen />}
      {screen === 'home' && rejoin && <RejoinCard />}
      {screen === 'training' && <TrainingScreen />}
      {screen === 'profile' && <ProfileScreen />}
      {screen === 'settings' && <SettingsScreen />}
      {screen === 'locker' && <LockerScreen />}
      {screen === 'faq' && <FaqScreen />}
      {screen === 'league' && <LeagueScreen />}
      {screen === 'leagues' && <LeaguesScreen />}
      {screen === 'country' && <CountryScreen />}
      {screen === 'plan' && <GamePlanScreen />}
      {screen === 'matchmaking' && <MatchmakingScreen />}
      {screen === 'vs' && <VsScreen />}
      {screen === 'fight' && (
        <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: '#0d0c0b' }} />}>
          <FightScreen />
        </Suspense>
      )}
      {screen === 'result' && <ResultScreen />}
      <PopupHost />
      {authSheet && <AuthSheet initialMode={authSheet} onClose={closeAuth} />}
    </>
  );
}
